import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent, MediaStartedPlayingEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * When a combined-listing vintage switch fires, `variant-picker` calls `updateMain`
   * which already morphs the entire <main> — including this gallery — before dispatching
   * the event. In that case `newProduct` is set on the event and we must skip the
   * redundant `replaceWith`; applying it would race with the morph and can produce a
   * blank gallery (the source document's gallery element may have been moved into the
   * live DOM by the morph, leaving `source.querySelector` returning null or a stale node).
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.newProduct) return;

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  /**
   * Auto-plays a video when the user navigates to its slide (thumbnail click, arrow, swipe).
   * Skips programmatic selections (variant changes) to avoid unexpected autoplay.
   *
   * Two paths:
   * - First visit: loadContent() starts playback but does not update isPlaying or show the
   *   toggle button — that's showDeferredMedia's job. We mirror that state manually by calling
   *   playMedia() afterwards so the toggle button becomes visible with the correct icon and
   *   pause/play interactions work correctly.
   * - Return visit: video is already in the DOM but paused. Dispatch MediaStartedPlayingEvent
   *   so any other currently-playing video pauses, then call playMedia() to resume.
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (!event.detail.userInitiated) return;

    const slide = event.detail.slide;
    if (!slide) return;

    /** @type {import('./media.js').DeferredMedia | null} */
    const deferredMedia = /** @type {any} */ (slide.querySelector('deferred-media'));
    if (!deferredMedia) return;

    if (!deferredMedia.hasAttribute('data-media-loaded')) {
      // First visit — loadContent handles template expansion, MediaStartedPlayingEvent dispatch,
      // and native video.play(). Call playMedia() to sync isPlaying state and reveal the toggle.
      deferredMedia.loadContent?.(false);
      deferredMedia.playMedia?.();
    }
  };
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
